import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { intelligenceApi, type AnsweredQuestion, type RejectedInsight } from '../api/intelligence'
import { insightCategoryLabel } from '../api/insights'
import { ApiError } from '../lib/api'
import { formatDateTimeBR } from '../lib/format'
import { fmtExactBR, fmtNumberBR, parseNumberBR, roasPercentHint } from '../lib/numberBR'
import { NumberPreview, useInvalidateIntelligence } from './IntelligenceInbox'
import { Spinner } from './Spinner'

type Tab = 'answered' | 'rejected' | 'dismissed'

const errText = (e: unknown) => (e instanceof ApiError ? e.message : 'Erro ao salvar.')

/** Alerta de uma resposta já gravada (hoje: ROAS digitado em %). */
function answerWarning(q: AnsweredQuestion): string | null {
  if (q.topic !== 'meta') return null
  return roasPercentHint(q.metric, q.targetValue ?? (q.answer ? parseNumberBR(q.answer) : null))
}

/**
 * O QUE JÁ SAIU DA CAIXA DA IA (últimos 90 dias): as respostas dadas (e o fato ou a meta
 * que viraram), os aprendizados rejeitados ("Está errado") e as perguntas descartadas
 * ("não se aplica"). Tudo pode ser corrigido ou desfeito daqui.
 */
export function IntelligenceHistory({ clientId }: { clientId?: string }) {
  const [tab, setTab] = useState<Tab>('answered')
  const { data, isLoading } = useQuery({
    queryKey: ['intelligence-history', clientId ?? 'all'],
    queryFn: () => intelligenceApi.history(clientId),
  })
  const questions = data?.questions ?? []
  const answered = questions.filter((q) => q.status === 'answered')
  const dismissed = questions.filter((q) => q.status === 'dismissed')
  const rejected = data?.rejected ?? []
  const warnings = answered.filter((q) => answerWarning(q)).length
  const showClient = !clientId

  const tabs: Array<{ key: Tab; label: string; count: number }> = [
    { key: 'answered', label: 'Respostas', count: answered.length },
    { key: 'rejected', label: '"Está errado"', count: rejected.length },
    { key: 'dismissed', label: '"Não se aplica"', count: dismissed.length },
  ]

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3">
        <h2 className="text-base font-semibold text-slate-900">↩️ O que já saiu da caixa</h2>
        <p className="text-xs text-slate-500">
          Respostas, aprendizados rejeitados e perguntas descartadas dos últimos 90 dias. Dá para corrigir uma
          resposta (o fato e a meta são refeitos) ou desfazer um "Está errado".
        </p>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              tab === t.key ? 'bg-slate-900 text-white' : 'border border-slate-300 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {t.label} ({t.count})
          </button>
        ))}
        {warnings > 0 && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
            ⚠️ {warnings} resposta{warnings === 1 ? '' : 's'} para conferir
          </span>
        )}
      </div>

      {isLoading ? (
        <Spinner />
      ) : tab === 'rejected' ? (
        rejected.length === 0 ? (
          <Empty text='Nenhum "Está errado" nos últimos 90 dias.' />
        ) : (
          <ul className="space-y-2">
            {rejected.map((r) => (
              <RejectedItem key={r.id} r={r} showClient={showClient} />
            ))}
          </ul>
        )
      ) : (tab === 'answered' ? answered : dismissed).length === 0 ? (
        <Empty text={tab === 'answered' ? 'Nenhuma resposta nos últimos 90 dias.' : 'Nenhuma pergunta descartada.'} />
      ) : (
        <ul className="space-y-2">
          {(tab === 'answered' ? answered : dismissed).map((q) => (
            <AnsweredItem key={q.id} q={q} showClient={showClient} />
          ))}
        </ul>
      )}
    </section>
  )
}

function Empty({ text }: { text: string }) {
  return <p className="rounded-lg border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500">{text}</p>
}

function ClientTag({ name }: { name: string }) {
  return <span className="rounded-full bg-slate-900/80 px-2 py-0.5 text-[11px] font-semibold text-white">{name}</span>
}

function AnsweredItem({ q, showClient }: { q: AnsweredQuestion; showClient: boolean }) {
  const [editing, setEditing] = useState(false)
  const warning = answerWarning(q)
  const parsed = q.kind === 'number' && q.answer ? parseNumberBR(q.answer) : null
  const isExec = q.topic === 'execucao'

  return (
    <li className={`rounded-lg border p-3 ${warning ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 bg-white'}`}>
      <div className="mb-1 flex flex-wrap items-center gap-1.5 text-[11px]">
        {showClient && <ClientTag name={q.clientName} />}
        <span className="rounded-full bg-violet-100 px-2 py-0.5 font-semibold text-violet-700">
          {isExec ? 'Isto foi feito?' : q.topic === 'meta' ? 'Meta' : 'Pergunta da IA'}
        </span>
        {q.campaign && <span className="rounded-full border border-slate-200 px-2 py-0.5 text-slate-500">{q.campaign}</span>}
        <span className="text-slate-400">
          {formatDateTimeBR(q.answeredAt)}
          {q.answeredBy ? ` · ${q.answeredBy}` : ''}
        </span>
      </div>
      <p className="text-sm text-slate-700">{q.question}</p>

      {q.status === 'dismissed' ? (
        <p className="mt-1 text-sm text-slate-500">Descartada como "não se aplica".</p>
      ) : (
        <div className="mt-1 space-y-0.5 text-sm">
          <p>
            <span className="text-slate-500">Resposta:</span> <strong className="text-slate-900">{q.answer}</strong>
            {/* Ponto é o caso ambíguo (milhar ou decimal?): mostra como foi lido. */}
            {parsed != null && q.answer?.includes('.') && (
              <span className="ml-1 text-xs text-slate-500">(lido como {fmtExactBR(parsed)})</span>
            )}
            {q.answerNote && <span className="text-slate-500"> — obs.: {q.answerNote}</span>}
          </p>
          {q.topic === 'meta' && q.metricLabel && (
            <p className="text-xs text-slate-600">
              → Meta de {q.metricLabel} ({q.metricBetterWhen === 'lower' ? 'máx.' : 'mín.'}):{' '}
              {q.targetValue != null ? <strong>{fmtNumberBR(q.targetValue)}</strong> : 'nenhuma gravada'}
            </p>
          )}
          {q.facts.map((f) => (
            <p key={f.id} className={`text-xs text-slate-600 ${f.active && f.status === 'confirmed' ? '' : 'opacity-60'}`}>
              → Fato no dossiê: {f.content}
              {!f.active ? ' (desativado)' : f.status === 'rejected' ? ' (rejeitado)' : ''}
            </p>
          ))}
          {warning && <p className="text-xs font-medium text-amber-800">⚠️ {warning}</p>}
        </div>
      )}

      {isExec ? (
        <p className="mt-1.5 text-[11px] text-slate-400">Para mudar, use o status da ação.</p>
      ) : editing ? (
        <AnswerEditor q={q} onDone={() => setEditing(false)} />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="mt-1.5 rounded-md border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          {q.status === 'dismissed' ? 'Responder agora' : '✎ Corrigir resposta'}
        </button>
      )}
    </li>
  )
}

/** Nova resposta para uma pergunta já respondida/descartada (refaz o fato e a meta). */
function AnswerEditor({ q, onDone }: { q: AnsweredQuestion; onDone: () => void }) {
  const invalidate = useInvalidateIntelligence()
  const [value, setValue] = useState(q.status === 'answered' && q.answer && q.kind !== 'choice' && q.kind !== 'yesno' ? q.answer : '')
  const [note, setNote] = useState(q.answerNote ?? '')
  const save = useMutation({
    mutationFn: (answer: string) => intelligenceApi.correct(q.id, answer, note),
    onSuccess: () => {
      invalidate()
      onDone()
    },
  })
  const busy = save.isPending
  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return
    e.preventDefault()
    if (value.trim() && !busy) save.mutate(value.trim())
  }

  return (
    <div className="mt-2 space-y-2 rounded-md border border-brand-200 bg-white p-2.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {q.kind === 'choice' || q.kind === 'yesno' ? (
          q.options.map((opt) => (
            <button
              key={opt}
              type="button"
              disabled={busy}
              onClick={() => save.mutate(opt)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold disabled:opacity-50 ${
                opt === q.answer
                  ? 'border-brand-600 bg-brand-600 text-white'
                  : 'border-brand-300 bg-white text-brand-700 hover:bg-brand-600 hover:text-white'
              }`}
            >
              {opt}
            </button>
          ))
        ) : (
          <>
            <input
              type="text"
              inputMode={q.kind === 'number' ? 'decimal' : undefined}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={onEnter}
              placeholder={q.kind === 'number' ? 'Só o número' : 'Sua resposta'}
              className={`${q.kind === 'number' ? 'w-32' : 'min-w-[220px] flex-1'} rounded-md border border-slate-300 px-2.5 py-1 text-sm focus:border-brand-500 focus:outline-none`}
            />
            <button
              type="button"
              disabled={busy || !value.trim()}
              onClick={() => save.mutate(value.trim())}
              className="rounded-md bg-brand-600 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              Salvar correção
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => save.mutate('Não sei')}
              className="rounded-full border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >
              Não sei
            </button>
          </>
        )}
        <button type="button" onClick={onDone} className="text-xs text-slate-500 hover:underline">
          cancelar
        </button>
        {busy && <Spinner className="h-4 w-4" />}
      </div>
      {q.kind === 'number' && <NumberPreview value={value} metric={q.topic === 'meta' ? q.metric : null} />}
      <input
        type="text"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
        placeholder="Comentário (opcional)"
        className="w-full rounded-md border border-slate-300 px-2.5 py-1 text-xs focus:border-brand-500 focus:outline-none"
      />
      <p className="text-[11px] text-slate-400">
        A correção substitui o fato que a resposta anterior gerou{q.topic === 'meta' ? ' e a meta do KPI' : ''}.
      </p>
      {save.isError && <p className="text-xs text-red-600">{errText(save.error)}</p>}
    </div>
  )
}

function RejectedItem({ r, showClient }: { r: RejectedInsight; showClient: boolean }) {
  const invalidate = useInvalidateIntelligence()
  const [editing, setEditing] = useState(false)
  const [showWhy, setShowWhy] = useState(false)
  const [content, setContent] = useState(r.content)
  const confirm = useMutation({
    mutationFn: (edits: { content?: string }) => intelligenceApi.confirm(r.id, edits),
    onSuccess: invalidate,
  })

  return (
    <li className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-1 flex flex-wrap items-center gap-1.5 text-[11px]">
        {showClient && <ClientTag name={r.clientName} />}
        <span className="rounded-full bg-rose-100 px-2 py-0.5 font-semibold text-rose-700">Está errado</span>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
          {r.source === 'site' ? 'lido no site' : 'aprendido pela IA'} · {insightCategoryLabel(r.category)}
        </span>
        <span className="text-slate-400">
          {formatDateTimeBR(r.reviewedAt)}
          {r.reviewedBy ? ` · ${r.reviewedBy}` : ''}
        </span>
      </div>
      {editing ? (
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={2}
          className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
        />
      ) : (
        <p className="text-sm text-slate-800">{r.content}</p>
      )}
      {r.evidence && (
        <div className="mt-1">
          <button type="button" onClick={() => setShowWhy((v) => !v)} className="text-xs text-slate-500 hover:underline">
            {showWhy ? 'esconder de onde veio' : 'de onde a IA tirou isso?'}
          </button>
          {showWhy && <p className="mt-1 rounded-md bg-slate-50 px-2 py-1 text-xs italic text-slate-600">{r.evidence}</p>}
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {editing ? (
          <>
            <button
              type="button"
              disabled={confirm.isPending || content.trim().length < 3}
              onClick={() => confirm.mutate({ content })}
              className="rounded-md bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              Salvar e confirmar
            </button>
            <button
              type="button"
              onClick={() => {
                setContent(r.content)
                setEditing(false)
              }}
              className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              Cancelar
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              disabled={confirm.isPending}
              onClick={() => confirm.mutate({})}
              className="rounded-md border border-emerald-300 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
            >
              ↺ Estava certo
            </button>
            <button
              type="button"
              disabled={confirm.isPending}
              onClick={() => setEditing(true)}
              className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              ✎ Corrigir e confirmar
            </button>
          </>
        )}
        {confirm.isPending && <Spinner className="h-4 w-4" />}
      </div>
      {confirm.isError && <p className="mt-1 text-xs text-red-600">{errText(confirm.error)}</p>}
    </li>
  )
}
